import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import RateReviewIcon from '@material-design-icons/svg/filled/rate_review.svg?react';
import { useState } from 'react';

import type { LoadDappReviews, SpotlightFeaturedReview } from '../types';
import { formatDate } from '../utils';
import { RatingStars } from './RatingStars';
import { ReviewPickerDialog } from './ReviewPickerDialog';

interface FeaturedReviewEditorProps {
  review: SpotlightFeaturedReview | null;
  onChange: (review: SpotlightFeaturedReview | null) => void;
  disabled?: boolean;
  dappId: string | null;
  appName: string;
  loadReviews: LoadDappReviews;
}

export function FeaturedReviewEditor({
  review,
  onChange,
  disabled = false,
  dappId,
  appName,
  loadReviews,
}: FeaturedReviewEditorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const picker =
    pickerOpen && dappId ? (
      <ReviewPickerDialog
        dappId={dappId}
        appName={appName}
        loadReviews={loadReviews}
        onClose={() => setPickerOpen(false)}
        onSelect={(picked) => {
          onChange(picked);
          setPickerOpen(false);
        }}
      />
    ) : null;

  if (!review) {
    if (disabled) {
      return null;
    }
    return (
      <>
        {dappId ? (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-400 transition-colors hover:text-teal-400"
          >
            <RateReviewIcon className="size-3.5 fill-current" />
            Feature a review
          </button>
        ) : (
          <span className="text-xs text-neutral-600">
            Reviews unavailable for this app
          </span>
        )}
        {picker}
      </>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-neutral-800 bg-neutral-950/40 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
            <RateReviewIcon className="size-3.5 fill-current" />
            Featured review
          </span>
          {!disabled && (
            <div className="flex items-center gap-2">
              {dappId && (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="text-xs font-medium text-neutral-400 hover:text-teal-400"
                >
                  Change
                </button>
              )}
              <button
                type="button"
                aria-label="Remove featured review"
                onClick={() => onChange(null)}
                className="flex size-6 items-center justify-center rounded text-neutral-500 hover:bg-neutral-800 hover:text-white"
              >
                <CloseIcon className="size-3.5 fill-current" />
              </button>
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <RatingStars rating={review.rating} />
          <span className="text-xs text-neutral-400">
            {review.authorDisplayName ?? 'Anonymous reviewer'}
          </span>
          {review.createdAt && (
            <span className="text-[11px] text-neutral-600">
              · {formatDate(review.createdAt)}
            </span>
          )}
        </div>

        <p className="mt-1.5 whitespace-pre-line text-sm text-neutral-200">
          {review.text}
        </p>

        <p className="mt-2 text-[11px] text-neutral-600">
          {review.reviewId ? 'From a real store review' : 'Custom review'}
        </p>
      </div>
      {picker}
    </>
  );
}
